// polyfills
import 'focus-options-polyfill';

import { withLDProvider } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { Normalize } from 'styled-normalize';
import { StyleSheetManager } from 'styled-components';
import { ApolloProvider } from '@apollo/react-hooks';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/router';

import { I18nextProvider } from 'react-i18next';
import { ErrorBoundary } from 'react-error-boundary';

import { useApollo } from 'utils/apollo/client';
import { StoresProvider } from 'contexts/stores';
import { ViewportProvider } from 'contexts/viewport';

import GlobalControllers from 'components/global-controllers';

// NOTE: GlobalModals is deprecated. we are in a process of refactoring to Modals
import GlobalModals from 'components/modals/global-modals';
import { Modals } from 'components/modals';

import Ernie from 'components/ernie';
import Fallback from 'components/fallbacks';
import BackToTop from 'components/back-to-top';
import { ConfigureRollbar } from 'components/configure-rollbar';
import { ErrorFallback } from 'src/errors';

import { GlobalStyles, EmbeddedStyles, ThemeProvider } from 'theme';
import useCreateStores from 'state';
import i18n from 'i18n';

import { useInitGlobalGA, useTrackRouteChanges } from 'hooks/use-google-analytics';
import { useInitLogRocket } from 'hooks/use-init-log-rocket';
import useFixLogRocket from 'shared/hooks/use-fix-log-rocket';
import PublicEnv from 'shared/utils/public-env';
import QueryParamProvider from 'utils/query-param-provider';
import { isPrerenderIoBot } from 'src/utils/prerender';

import 'react-multi-carousel/lib/styles.css';
import { Analytics } from 'utils/analytics';

// export function reportWebVitals(metric) {
//   console.log(metric);
//   if (window && window.Rollbar?.info) {
//     window.Rollbar.info(metric);
//   }
// }

// This will wrap every single page component
// this will *also* wrap every single storybook component!
function App({ Component, pageProps = {}, deviceType = `desktop` }) {
  // first thing's first....
  useFixLogRocket();
  const stores = useCreateStores();
  const apolloClient = useApollo(pageProps.initialApolloState);
  const router = useRouter();
  const isEmbedded = useObserver(() => stores.UI.isEmbedded);
  const Layout = Component.layout || React.Fragment;

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector(`#jss-server-side`);
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  // Enable Tracking Services
  useInitLogRocket(stores.UI);
  useInitGlobalGA();
  useTrackRouteChanges();

  return (
    <I18nextProvider i18n={i18n.i18n}>
      <QueryParamProvider>
        <ConfigureRollbar router={router} ui={stores.UI} user={stores.User} />
        <StoresProvider value={stores}>
          <StyleSheetManager disableCSSOMInjection={isPrerenderIoBot()}>
            <ViewportProvider>
              <Normalize />
              <GlobalStyles />
              {isEmbedded && <EmbeddedStyles />}
              {router.isFallback && <Fallback />}
              {!router.isFallback && (
                <ApolloProvider client={apolloClient}>
                  <ThemeProvider deviceType={deviceType}>
                    <Analytics />
                    <GlobalControllers />
                    <GlobalModals />
                    <Modals />
                    <Ernie />
                    {isEmbedded && <BackToTop />}
                    <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[router.asPath]}>
                      <Layout {...pageProps}>
                        <Component {...pageProps} />
                      </Layout>
                    </ErrorBoundary>
                  </ThemeProvider>
                </ApolloProvider>
              )}
            </ViewportProvider>
          </StyleSheetManager>
        </StoresProvider>
      </QueryParamProvider>
    </I18nextProvider>
  );
}

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// App.getInitialProps = async (appContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await NextApp.getInitialProps(appContext);

//   return {
//     ...appProps,
//   };
// };

// export default appWithTranslation(App);

export default withLDProvider({
  clientSideID: PublicEnv.launchDarklyClientId,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  user: {
    email: 'engineering@dutchie.com',
    key: 'eng-internal',
  },
})(App);
